import { Link } from "react-router";
import {
  ExternalLink,
  FileText,
  Globe,
  MoreVertical,
  RefreshCw,
} from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LinkedInStatusBadge } from "./LinkedInStatusBadge";
import { JobProgressCell } from "./LinkedInJobProgress";
import {
  jobTemSuspeitaFreelancer,
  type LinkedInJob,
  type LinkedInJobStatus,
} from "@/types/linkedin";
import { FreelancerLinkedInButton } from "@/components/freelancers/painel/FreelancerLinkedInButton";
import { formatDateTime } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";

interface Props {
  jobs: LinkedInJob[];
  onAssociate: (job: LinkedInJob) => void;
  onReview: (job: LinkedInJob) => void;
  onReprocess: (job: LinkedInJob) => void;
  /** Repete a busca automática do perfil (job source=scraping que falhou). */
  onRescrape: (job: LinkedInJob) => void;
}

/** "há X dias/meses" pra deixar óbvio o que está parado faz tempo. */
function tempoDesde(iso: string): string {
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (dias <= 0) return "hoje";
  if (dias === 1) return "ontem";
  if (dias < 30) return `há ${dias} dias`;
  const meses = Math.floor(dias / 30);
  return meses === 1 ? "há 1 mês" : `há ${meses} meses`;
}

/** Handle do perfil pra exibição compacta (linkedin.com/in/<handle>). */
function handleDoPerfil(urlPerfil: string): string {
  const match = urlPerfil.match(/linkedin\.com\/in\/([^/?#]+)/i);
  if (!match) return urlPerfil;
  try {
    return `in/${decodeURIComponent(match[1])}`;
  } catch {
    return `in/${match[1]}`;
  }
}

/**
 * URL segura pro href do perfil: só aceita https no domínio do LinkedIn
 * (o valor vem do banco — sem este guard, uma linha adulterada viraria XSS
 * via `javascript:`).
 */
function urlSeguraDoPerfil(urlPerfil: string): string | null {
  return /^https:\/\/([a-z0-9-]+\.)?linkedin\.com\//i.test(urlPerfil)
    ? urlPerfil
    : null;
}

// Status em que faz sentido reprocessar (re-roda a extração no mesmo PDF).
// Fora: jobs em andamento (ainda processando). Já aplicados também podem ser
// reprocessados — gera novo diff e exige aplicar de novo.
const REPROCESSABLE: ReadonlySet<LinkedInJobStatus> = new Set([
  "review_ready",
  "no_changes",
  "awaiting_match",
  "failed",
  "cancelled",
  "applied",
]);

// Rótulos pt-BR da senioridade inferida (enum do extractor).
const SENIORIDADE_LABELS: Record<string, string> = {
  estagio: "Estágio",
  junior: "Júnior",
  pleno: "Pleno",
  senior: "Sênior",
  diretor: "Diretor",
  cl_level: "C-level",
};

// Status em que o pipeline está trabalhando agora — só eles mostram a barra
// de progresso; o resto (aguardando revisão/associação, aplicado, falha)
// mostra só o status final com o motivo.
const STATUS_EM_PROCESSAMENTO: ReadonlySet<LinkedInJobStatus> = new Set([
  "pending",
  "uploading",
  "extracting",
  "enriching",
  "matching",
  "diffing",
]);

// Rótulos curtos do MOTIVO da revisão manual (códigos do auto_review).
const MOTIVO_REVISAO_LABELS: Record<string, string> = {
  sinal_freelancer: "Possível freelancer",
  empresa_propria: "Possível empresa própria",
  multiplas_empresas_atuais: "Várias empresas atuais",
  conflito_linkedin_url: "URL de outro freelancer",
  extracao_incompleta: "Extração incompleta",
  sem_criterio_auto: "Empresa não reconhecida",
  atual_a_confirmar: "Emprego atual a confirmar",
  auto_apply_desligado: "Aplicação automática desligada",
};

/** Códigos distintos do motivo de revisão manual (ordem do classificador). */
function motivosDeRevisao(job: LinkedInJob): Array<{ code: string; detail: string }> {
  if (job.status !== "review_ready" || job.auto_review?.mode !== "manual") return [];
  const vistos = new Set<string>();
  const distintos: Array<{ code: string; detail: string }> = [];
  for (const razao of job.auto_review.reasons ?? []) {
    if (vistos.has(razao.code)) continue;
    vistos.add(razao.code);
    distintos.push(razao);
  }
  return distintos;
}

export function LinkedInJobsTable({
  jobs,
  onAssociate,
  onReview,
  onReprocess,
  onRescrape,
}: Props) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-40">Ações</TableHead>
          <TableHead>Origem</TableHead>
          <TableHead className="w-36">Quando</TableHead>
          <TableHead>Capturado</TableHead>
          <TableHead className="w-56">Status</TableHead>
          <TableHead>Mudanças</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {jobs.length === 0 && (
          <TableRow>
            <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
              Nada processado ainda. Suba um PDF ou inicie uma busca automática.
            </TableCell>
          </TableRow>
        )}
        {jobs.map((job) => {
          const counts = job.diff_summary?.counts;
          const sm = job.summary;
          return (
            <TableRow key={job.id}>
              <TableCell className="align-middle">
                <div className="flex items-center gap-1">
                  {/* Ação primária — depende do status (some quando o status muda) */}
                  {job.status === "awaiting_match" && (
                    <Button size="sm" onClick={() => onAssociate(job)}>
                      Associar
                    </Button>
                  )}
                  {job.status === "review_ready" && (
                    <Button size="sm" onClick={() => onReview(job)}>
                      Revisar
                    </Button>
                  )}
                  {job.source === "scraping" &&
                    job.status === "failed" &&
                    job.freelancer_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onRescrape(job)}
                        title="Busca o perfil de novo e cria um novo processamento"
                      >
                        Buscar novamente
                      </Button>
                    )}
                  {(job.status === "applied" || job.status === "no_changes") &&
                    job.extracted_at && (
                      <Button size="sm" variant="outline" onClick={() => onReview(job)}>
                        Ver extração
                      </Button>
                    )}

                  {/* Ações secundárias (menu três pontinhos). Busca que falhou
                      não tem conteúdo pra reprocessar — a ação é buscar de novo. */}
                  {REPROCESSABLE.has(job.status) &&
                    !(job.source === "scraping" && job.status === "failed") && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label="Mais ações"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem onClick={() => onReprocess(job)}>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Reprocessar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex max-w-[280px] flex-col gap-1">
                  <Badge
                    variant="outline"
                    className={cn(
                      "w-fit text-[10px] font-normal",
                      job.source === "scraping"
                        ? "border-sky-200 bg-sky-50 text-sky-800"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {job.source === "scraping" ? (
                      <Globe className="mr-1 h-3 w-3" />
                    ) : (
                      <FileText className="mr-1 h-3 w-3" />
                    )}
                    {job.source === "scraping" ? "Busca automática" : "Upload de PDF"}
                  </Badge>
                  {job.source === "scraping" ? (
                    urlSeguraDoPerfil(job.original_filename) ? (
                      <a
                        href={urlSeguraDoPerfil(job.original_filename)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex w-fit items-center gap-1.5 text-xs text-muted-foreground hover:text-primary"
                        title={`Abrir perfil: ${job.original_filename}`}
                      >
                        <Linkedin className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate font-mono">
                          {handleDoPerfil(job.original_filename)}
                        </span>
                      </a>
                    ) : (
                      <span className="flex w-fit items-center gap-1.5 text-xs text-muted-foreground">
                        <Linkedin className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate font-mono">
                          {handleDoPerfil(job.original_filename)}
                        </span>
                      </span>
                    )
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span
                        className="truncate font-mono text-xs"
                        title={job.original_filename}
                      >
                        {job.original_filename}
                      </span>
                      {job.s3_url && (
                        <a
                          href={job.s3_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 text-muted-foreground hover:text-primary"
                          title="Abrir PDF"
                          aria-label="Abrir PDF"
                        >
                          <FileText className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    {job.freelancer_id && (
                      <Button asChild variant="link" className="h-auto p-0 text-xs">
                        <Link to={`/freelancers/${job.freelancer_id}`}>
                          Ver freelancer <ExternalLink className="ml-1 h-3 w-3" />
                        </Link>
                      </Button>
                    )}
                    {job.source !== "scraping" && (
                      <FreelancerLinkedInButton
                        freelancerId={job.freelancer_id}
                        url={sm?.linkedin_url}
                        variant="inline"
                        label="LinkedIn"
                        className="w-fit text-xs text-primary hover:underline"
                        iconClassName="h-3 w-3"
                      />
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell className="align-top">
                <div className="space-y-0.5 text-xs">
                  <div className="whitespace-nowrap">
                    {formatDateTime(job.created_at)}
                  </div>
                  <div className="text-muted-foreground">
                    {tempoDesde(job.created_at)}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                {sm && (sm.nome_completo || sm.empresa_atual) ? (
                  <div className="max-w-[420px] space-y-0.5">
                    {sm.nome_completo && (
                      <div className="text-xs font-medium truncate" title={sm.nome_completo}>
                        {sm.nome_completo}
                      </div>
                    )}
                    {sm.headline && (
                      <div className="text-xs text-muted-foreground truncate" title={sm.headline}>
                        {sm.headline}
                      </div>
                    )}
                    {sm.empresa_atual && (
                      <div
                        className="text-[11px] text-muted-foreground truncate"
                        title={[sm.cargo_atual, sm.empresa_atual].filter(Boolean).join(" · ")}
                      >
                        {sm.cargo_atual ? `${sm.cargo_atual} · ` : ""}
                        <span className="font-medium">{sm.empresa_atual}</span>{" "}
                        {sm.is_atual_a_confirmar ? (
                          <span className="text-amber-700" title="Emprego atual inferido — confirmar na revisão">
                            (atual?)
                          </span>
                        ) : (
                          <span className="text-green-700">(atual)</span>
                        )}
                      </div>
                    )}
                    {(sm.senioridade || sm.anos_experiencia != null || sm.localizacao) && (
                      <div className="text-[11px] text-muted-foreground truncate">
                        {[
                          sm.senioridade
                            ? SENIORIDADE_LABELS[sm.senioridade] ?? sm.senioridade
                            : null,
                          sm.anos_experiencia != null
                            ? `≈ ${Math.round(sm.anos_experiencia)} anos de experiência`
                            : null,
                          sm.localizacao,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground text-xs">—</span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex flex-col items-start gap-1">
                  {STATUS_EM_PROCESSAMENTO.has(job.status) ? (
                    <JobProgressCell job={job} />
                  ) : (
                    <>
                      <LinkedInStatusBadge status={job.status} />
                      {job.status === "failed" && job.error_message && (
                        <span
                          className="text-[11px] text-red-700"
                          title={job.error_message}
                        >
                          {job.error_message.slice(0, 60)}
                        </span>
                      )}
                      {job.status === "applied" && job.review_mode === "auto" && (
                        <span
                          className="text-[11px] text-emerald-700"
                          title="Aplicado pela IA sem revisão humana (critérios de revisão automática)"
                        >
                          Aplicado automaticamente
                        </span>
                      )}
                      {job.status === "applied" && job.review_mode === "human" && (
                        <span className="text-[11px] text-muted-foreground">
                          Revisado manualmente
                        </span>
                      )}
                      {job.summary?.is_freelancer_declarado === true && (
                        <span
                          className="text-[11px] font-medium text-emerald-700"
                          title="O perfil se declara freelancer explicitamente (registrado na disponibilidade ao aplicar)."
                        >
                          Freelancer declarado
                        </span>
                      )}
                      {/* Motivos da revisão manual (todos, não só freelancer) */}
                      {motivosDeRevisao(job).map((razao) => (
                        <span
                          key={razao.code}
                          className="text-[11px] text-amber-700"
                          title={razao.detail}
                        >
                          {MOTIVO_REVISAO_LABELS[razao.code] ?? razao.detail}
                        </span>
                      ))}
                      {/* Fallback pra jobs antigos sem auto_review */}
                      {!job.auto_review &&
                        job.status !== "applied" &&
                        job.summary?.is_freelancer_declarado !== true &&
                        jobTemSuspeitaFreelancer(job) && (
                          <span className="text-[11px] text-amber-700">
                            Possível freelancer
                          </span>
                        )}
                    </>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {counts && (counts.added + counts.changed + counts.removed > 0) ? (
                  <span className="text-xs text-muted-foreground">
                    {counts.added > 0 && <span className="text-green-700">+{counts.added}</span>}
                    {counts.changed > 0 && <span className="ml-2 text-yellow-700">~{counts.changed}</span>}
                    {counts.removed > 0 && <span className="ml-2 text-red-700">−{counts.removed}</span>}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
